import { flatten } from 'flat';
import { DateTime } from 'luxon';

import type { GetTravelCategoriesByMarketResults } from '../components/MarketPage/MarketPage.types';
import type { SanityUUID } from '../data/sanity/types';
import type { RecipeQl } from '../dtos/smart-search/types';

export * from './stringHelpers';
export * from './typeHelpers';
export * from './urlHelpers';

export const isSSR = () => typeof window === 'undefined';

export const flattenPropertiesAsStrings = <
  T extends { [key: string | number]: unknown } | null | undefined,
>(
  obj: T
) => {
  if (!obj) {
    return {};
  }

  return propertiesAsStrings(
    flatten(obj) as { [key: string | number]: unknown }
  );
};

export const propertiesAsStrings = <
  T extends { [key: string | number]: unknown } | null | undefined,
>(
  source: T,
  excludeProperties: string[] = []
): { [key: string]: string } => {
  if (!source) {
    return {};
  }

  return Object.keys(source).reduce(
    (acc: { [key: string]: string }, key: string) => {
      const value = source[key];

      if (excludeProperties.includes(key)) {
        return acc;
      }

      if (typeof value === 'string') {
        acc[key] = value;
      }

      if (typeof value === 'number' || typeof value === 'boolean') {
        acc[key] = value.toString();
      }

      return acc;
    },
    {}
  );
};

export const mapArrayToBooleanFlags = <T>(
  array: T[] | undefined | null,
  prefix: string,
  property: keyof T
): Record<string, string> => {
  if (!array) return {};

  return array.reduce(
    (acc, item) => ({
      ...acc,
      [`${prefix}_${item[property]}`]: 'true',
    }),
    {} as Record<string, string>
  );
};

export const extractUniqueTripIdsFromTripCollections = (
  collections: { tripIds?: SanityUUID[] }[] | null | undefined,
  limit?: number
): SanityUUID[] =>
  Array.from(
    new Set(
      (collections ?? []).flatMap((collection) => {
        const tripIds = collection?.tripIds ?? [];

        return limit ? tripIds.slice(0, limit) : tripIds;
      })
    )
  );

export const removeNestedPropertyFromObject = (
  obj: any,
  propertyToRemove: string
): any => {
  const removeFn = (obj: any): any => {
    if (Array.isArray(obj)) {
      return obj.map((item) => removeFn(item));
    } else if (typeof obj === 'object' && obj !== null) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [propertyToRemove]: _, ...rest } = obj;

      for (const key in rest) {
        rest[key] = removeFn(rest[key]);
      }

      return rest;
    }

    return obj;
  };

  return removeFn(obj);
};

export const formatPrice = (
  amount: number | undefined,
  locale: string | undefined,
  currency: string | undefined | null
): string | undefined => {
  return amount?.toLocaleString(locale, {
    style: 'currency',
    currency: currency ?? undefined,
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  });
};

export const safeJsonParse = <T>(obj: any): T => {
  return JSON.parse(JSON.stringify(obj)) as T;
};

export const getFirstPolygonId = (recipe?: RecipeQl): number | undefined => {
  if (!recipe?.ingredients?.length) {
    return undefined;
  }

  for (const ingredient of recipe.ingredients) {
    if (ingredient?.polygonId) {
      return ingredient.polygonId;
    }
    if (ingredient?.destination?.startsWith('polygon-id:')) {
      const id = parseInt(
        ingredient.destination.replace('polygon-id:', ''),
        10
      );
      if (!isNaN(id)) {
        return id;
      }
    }
  }

  return undefined;
};

export const getFullDayRange = (
  firstArrivalDate: string,
  lastArrivalDate: string
): { startDate: string | null; endDate: string | null } => {
  const startDate = DateTime.fromISO(firstArrivalDate).toISODate();
  const endDate = DateTime.fromISO(lastArrivalDate).toISODate();

  return { startDate, endDate };
};

export const getParsedTravelCategories = (
  travelCategories: GetTravelCategoriesByMarketResults | null | undefined
) => {
  if (!travelCategories) {
    return [];
  }

  return travelCategories.map((tc) => {
    switch (tc._type) {
      case 'campaign':
        return {
          name: tc.label || tc.headline,
          url: `/c/${tc.slug}`,
        };
      case 'tag':
      default:
        return {
          name: tc.name,
          url: `/tag/${tc.slug}`,
        };
    }
  });
};
